import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import ConversationList from '../components/Chat/ConversationList';
import ChatWindow from '../components/Chat/ChatWindow';
import chatService from '../services/chatService';
import { messageService } from '../services/api';
import './MessagesPage.css';

const MessagesPage = () => {
  const { currentUser } = useAuth();
  const { userId } = useParams(); // Para cargar chat específico desde URL
  const navigate = useNavigate();
  
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Inicializar el servicio de chat y manejadores de eventos
  useEffect(() => {
    // Si no hay usuario logueado, redirigir a login
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    // Cargar las conversaciones iniciales
    loadConversations();
    
    // Nota: No inicializamos servicio WebSocket para evitar problemas de CSP
    // Los mensajes se enviarán/recibirán a través de APIs normales
    
    return () => {
      // Limpiar recursos cuando se desmonte el componente
    };
  }, [currentUser, navigate]);
  
  // Cargar conversación específica si se proporciona userId en la URL
  useEffect(() => {
    if (userId && conversations.length > 0) {
      const conversation = conversations.find(conv => conv.user._id === userId);
      if (conversation) {
        selectConversation(conversation);
      }
    }
  }, [userId, conversations]);
  
  // Función para cargar las conversaciones del usuario
  const loadConversations = async () => {
    try {
      setLoading(true);
      
      // En un caso real, esto vendría del backend
      // Por ahora, simulamos las conversaciones
      setTimeout(() => {
        const mockConversations = [
          {
            id: '1',
            user: {
              _id: '2',
              name: 'Juan Pérez',
              profilePicture: null
            },
            task: {
              _id: '101',
              title: 'Diseño de logo'
            },
            lastMessage: {
              message: 'Me gustaría saber si puedes hacer el logo en formato vectorial',
              sentAt: new Date(Date.now() - 3600000).toISOString(),
              sender: '2'
            },
            unread: 1
          },
          {
            id: '2',
            user: {
              _id: '3',
              name: 'María López',
              profilePicture: null
            },
            task: {
              _id: '102',
              title: 'Desarrollo web'
            },
            lastMessage: {
              message: 'He terminado la primera fase del proyecto',
              sentAt: new Date(Date.now() - 86400000).toISOString(),
              sender: currentUser.id
            },
            unread: 0
          }
        ];
        
        setConversations(mockConversations);
        setLoading(false);
        
        // Si hay un userId en la URL y corresponde a una conversación, activarla
        if (userId) {
          const targetConv = mockConversations.find(conv => conv.user._id === userId);
          if (targetConv) {
            selectConversation(targetConv);
          }
        }
      }, 500);
    } catch (err) {
      setError('Error al cargar conversaciones');
      setLoading(false);
      console.error('Error al cargar conversaciones:', err);
    }
  };
  
  // Función para seleccionar una conversación
  const selectConversation = async (conversation) => {
    try {
      setActiveConversation(conversation);
      
      // Cargar historial de mensajes
      loadChatHistory(currentUser.id, conversation.user._id);
      
      // Marcar como leídos (en un caso real, esto iría al backend)
      if (conversation.unread > 0) {
        const updatedConversations = conversations.map(conv => {
          if (conv.id === conversation.id) {
            return { ...conv, unread: 0 };
          }
          return conv;
        });
        setConversations(updatedConversations);
      }
      
      // Actualizar URL si es necesario
      const currentPath = window.location.pathname;
      const expectedPath = `/messages/${conversation.user._id}`;
      
      if (currentPath !== expectedPath) {
        navigate(expectedPath, { replace: true });
      }
    } catch (err) {
      setError('Error al seleccionar conversación');
      console.error('Error al seleccionar conversación:', err);
    }
  };
  
  // Cargar historial de chat
  const loadChatHistory = async (userId1, userId2) => {
    try {
      setLoading(true);
      
      // En un caso real, esto haría una llamada al backend
      // Por ahora simulamos los mensajes
      setTimeout(() => {
        const mockMessages = [
          {
            _id: '1',
            message: 'Hola, me interesa tu tarea de diseño de logo',
            sender: currentUser.id,
            receiver: userId2,
            sentAt: new Date(Date.now() - 7200000).toISOString()
          },
          {
            _id: '2',
            message: 'Hola! Claro, ¿qué tipo de logo necesitas?',
            sender: userId2,
            receiver: currentUser.id,
            sentAt: new Date(Date.now() - 7000000).toISOString()
          },
          {
            _id: '3',
            message: 'Necesito un logo para mi nueva empresa de tecnología',
            sender: currentUser.id,
            receiver: userId2,
            sentAt: new Date(Date.now() - 6800000).toISOString()
          },
          {
            _id: '4',
            message: 'Me gustaría saber si puedes hacer el logo en formato vectorial',
            sender: userId2,
            receiver: currentUser.id,
            sentAt: new Date(Date.now() - 3600000).toISOString()
          }
        ];
        
        setMessages(mockMessages);
        setLoading(false);
      }, 300);
    } catch (err) {
      setError('Error al cargar historial de chat');
      setLoading(false);
      console.error('Error al cargar historial de chat:', err);
    }
  };
  
  // Enviar un mensaje nuevo - versión simplificada sin WebSockets
  const handleSendMessage = async (messageText) => {
    if (!activeConversation) return;
    
    try {
      const sender = currentUser.id;
      const receiver = activeConversation.user._id;
      
      // Mensaje temporal optimista (se muestra inmediatamente)
      const tempMessage = {
        _id: `temp-${Date.now()}`,
        message: messageText,
        sender,
        receiver,
        sentAt: new Date().toISOString(),
        isPending: true
      };
      
      // Actualizar UI inmediatamente (optimistic UI)
      setMessages(prevMessages => [...prevMessages, tempMessage]);
      
      // Enviar mensaje a través del servicio de API (no WebSocket)
      await messageService.sendMessage({ sender, receiver, message: messageText });
      
      // Actualizar la última conversación
      setConversations(prevConversations => 
        prevConversations.map(conv => {
          if (conv.id === activeConversation.id) {
            return {
              ...conv,
              lastMessage: {
                message: messageText,
                sentAt: new Date().toISOString(),
                sender
              }
            };
          }
          return conv;
        })
      );
      
      // Reemplazar el mensaje temporal con uno confirmado
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg._id === tempMessage._id 
            ? { ...tempMessage, isPending: false, _id: Date.now().toString() } 
            : msg
        )
      );
    } catch (err) {
      setError('Error al enviar mensaje');
      console.error('Error al enviar mensaje:', err);
      
      // Marcar el mensaje como fallido
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg._id.startsWith('temp-') 
            ? { ...msg, error: true } 
            : msg
        )
      );
    }
  };
  
  // Manejador de mensaje entrante (desde WebSocket)
  const handleIncomingMessage = (message) => {
    // Verificar si el mensaje es de la conversación activa
    const isActiveChat = activeConversation && 
      (message.sender === activeConversation.user._id || 
       message.receiver === activeConversation.user._id);
    
    if (isActiveChat) {
      // Añadir mensaje a la conversación activa
      setMessages(prevMessages => [
        ...prevMessages.filter(msg => msg._id !== message._id), // Evitar duplicados
        message
      ]);
    }
    
    // Actualizar la lista de conversaciones
    updateConversationWithMessage(message);
  };
  
  // Actualizar conversación con un mensaje nuevo
  const updateConversationWithMessage = (message) => {
    const otherUserId = message.sender === currentUser.id 
      ? message.receiver 
      : message.sender;
    
    // Buscar si ya existe la conversación
    const existingConvIndex = conversations.findIndex(
      conv => conv.user._id === otherUserId
    );
    
    if (existingConvIndex >= 0) {
      // Actualizar conversación existente
      const updatedConversations = [...conversations];
      
      // Si el mensaje es nuevo y no es del usuario actual, incrementar contador
      const isIncomingMessage = message.sender !== currentUser.id;
      const isNotActiveChat = !activeConversation || 
                             activeConversation.user._id !== otherUserId;
      
      updatedConversations[existingConvIndex] = {
        ...updatedConversations[existingConvIndex],
        lastMessage: {
          message: message.message,
          sentAt: message.sentAt || message.timestamp || new Date().toISOString(),
          sender: message.sender
        },
        unread: isIncomingMessage && isNotActiveChat 
          ? (updatedConversations[existingConvIndex].unread || 0) + 1
          : updatedConversations[existingConvIndex].unread || 0
      };
      
      setConversations(updatedConversations);
    } else {
      // Idealmente, buscaríamos la info del usuario y la tarea en el backend
      // Por ahora, añadimos una conversación con info mínima
      // En un caso real, haríamos una llamada a la API
    }
  };
  
  // Manejador de evento al unirse a una sala
  const handleJoinRoom = (data) => {
    console.log(`Unido a sala de chat: ${data.room}`);
  };
  
  // Manejador de errores de chat
  const handleChatError = (error) => {
    setError(`Error en chat: ${error.message || 'Error desconocido'}`);
    console.error('Error en chat:', error);
  };
  
  return (
    <div className="container-fluid messages-container">
      <div className="row h-100">
        {/* Columna de lista de conversaciones */}
        <div className="col-md-4 col-lg-3 p-0 messages-sidebar">
          <ConversationList 
            conversations={conversations}
            activeConversation={activeConversation}
            onSelectConversation={selectConversation}
          />
        </div>
        
        {/* Columna de ventana de chat */}
        <div className="col-md-8 col-lg-9 p-0 messages-main">
          <ChatWindow 
            conversation={activeConversation}
            messages={messages}
            onSendMessage={handleSendMessage}
          />
        </div>
      </div>
      
      {/* Modal de error */}
      {error && (
        <div className="alert alert-danger error-alert">
          {error}
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setError(null)}
          ></button>
        </div>
      )}
      
      {/* Indicador de carga */}
      {loading && (
        <div className="loading-overlay">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesPage; 